import { chain, filter, find, get, isEmpty, isEqual, includes, map, omit, reject, some } from 'lodash'
import { createSelector } from '@reduxjs/toolkit'
import semverValid from 'semver/functions/valid'
import semverGte from 'semver/functions/gte'

const I2C_SCAN_VERSION = '1.0.0-beta.16'

export const
  currentCheckin = state => state.wprsnpr?.checkin || {},

  allBoards = state => state.wprsnpr.boards || {},

  allComponents = state => state.wprsnpr.componentTypes || {},

  currentSemver = state => state.wprsnpr.currentSemver,

  pinComponentTypes = createSelector([allComponents], components => {
    return filter(Object.values(components), "isPin")
  }),

  i2cComponentTypes = createSelector([allComponents], components => {
    return filter(Object.values(components), "isI2C")
  }),

  servoComponentTypes = createSelector([allComponents], components => {
    return filter(Object.values(components), "isServo")
  }),

  ds18x20ComponentTypes = createSelector([allComponents], components => {
    return filter(Object.values(components), "isDS18X20")
  }),

  currentCheckinBoard = createSelector([currentCheckin, allBoards], (checkin, boards) => {
    return checkin.machineName ?
      boards[checkin.machineName] :
      {}
  }),

  // Groups with a machine_name are actually wippers
  allWippers = state => filter(get(state, "groups.groups", []), "machine_name"),

  currentWipper = state => {
    const maybeWipper = get(state, "groups.group", {})

    return maybeWipper.machine_name ? maybeWipper : null
  },

  currentBoard = createSelector([currentWipper, allBoards], (wipper, boards) => {
    return wipper ?
      boards[wipper.machine_name] :
      {}
  }),

  currentWipperComponents = createSelector([currentWipper], wipper => wipper?.feeds || []),

  currentComponents = state => state.wprsnpr?.currentComponents || [],

  currentComponent = state => state.wprsnpr?.currentComponent,

  currentComponentType = createSelector(
    [currentComponent, currentComponents, allComponents],
    (component, components, types) => {
      const
        firstComponent = component?.wipper_pin_info || components?.[0],
        type = firstComponent?.type

      if(!type) { return null }

      const
        foundType = types[type],
        foundParentType = find(Object.values(types), ({ subcomponents }) => {
          return some(subcomponents, { type })
        })

      return foundType || foundParentType || null
    }
  ),

  currentComponentTypeI2CAddresses = createSelector([currentComponentType], type => {
    // they're string hex in the JSON, parse to ints
    return map(type.i2cAddresses || [], address => parseInt(address, 16))
  }),

  nextComponentKey = createSelector([currentWipperComponents], components => {
    const
      maxKey = chain(components)
        .map("key")                          // just keys
        .map(key => /ws-(\d{3,})/.exec(key)) // keys matching ws-###...
        .compact()                           // throw away non-matches
        .map(matches => matches[1])          // just the ### part of matches
        .map(match => parseInt(match, 10))   // convert from string to integer
        .compact()                           // discard NaNs from parseInt
        .max()                               // what's the highest number?
      .value() || 0,                         // return it, default to 0
      nextKey = (maxKey + 1).toString()      // add 1 and convert to string

    // pad it with zeroes as appropriate
    switch(nextKey.length) {
      case 1:
        return `ws-00${nextKey}`
      case 2:
        return `ws-0${nextKey}`
      default:
        return `ws-${nextKey}`
    }
  }),

  connections = state => state.wprsnpr.connections,

  isOnline = (state, deviceKey) => includes(connections(state), deviceKey),

  currentWipperConnected = createSelector(
    [currentWipper, connections],
    (wipper, connections) => includes(connections, wipper?.key)
  ),

  currentComponentHasCosmeticChangesOnly = createSelector(
    [currentComponent, currentWipperComponents],
    (editedComponent, allComponents) => {
      const
        // find the matching component by key
        existingComponent = find(allComponents, { key: editedComponent.key }),
        // only compare wipper_pin_info, except for the "visualization" key
        existingNonCosmeticInfo = omit(existingComponent.wipper_pin_info, ["visualization"]),
        editedNonCosmeticInfo = omit(editedComponent.wipper_pin_info, ["visualization"])

      return isEqual(existingNonCosmeticInfo, editedNonCosmeticInfo)
    }
  ),

  currentWipperSemver = createSelector([ currentWipper ], wipper => wipper?.wipper_semver ),

  wipperKeys = createSelector([allWippers], wippers => map(wippers, "key")),

  hasWipperByDeviceId = (state, deviceId) => {
    const keys = wipperKeys(state)
    return includes(keys, deviceId)
  },

  // Returns the analog and digital pin collections from the Board definition
  // with a "used" boolean added that reflects whether the pin already has
  // a wipper component connected to it
  currentPinsWithStatus = createSelector([currentBoard, currentWipperComponents], (board, components) => {
    const
      { digitalPins, analogPins } = board.components,
      usedPinNames = map(components, "wipper_pin_info.pinName"),
      addUsedMapper = collection => map(collection, pin => ({
        ...pin,
        used: includes(usedPinNames, pin.name)
      })),
      digital = addUsedMapper(digitalPins),
      analog = addUsedMapper(analogPins),
      allPins = digital.concat(analog),
      pwm = filter(allPins, 'hasPWM'),
      servo = filter(allPins, 'hasServo'),
      ds18x20 = digital

    return { analog, digital, pwm, servo, ds18x20 }
  }),

  availablePins = createSelector([currentPinsWithStatus], currentPins => ({
    analog: reject(currentPins.analog, "used"),
    digital: reject(currentPins.digital, "used"),
  })),

  currentComponentPinType = createSelector([currentComponentType], componentType => {
    const { mode, isServo, isPWM, isDS18X20 } = componentType

    if(mode === 'ANALOG') {
      return 'analog'
    } else if(mode === 'DIGITAL') {
      return 'digital'
    } else if(isServo) {
      return 'servo'
    } else if(isPWM) {
      return 'pwm'
    } else if(isDS18X20) {
      return 'ds18x20'
    }

    return ''
  }),

  currentOptionsForPinSelect = createSelector([currentComponentPinType, currentPinsWithStatus],
    (pinType, currentPinsWithStatus) =>  {
      const
        pins = currentPinsWithStatus[pinType] || [],
        options = map(pins, ({ name, displayName, used }) => ({
          id: name,
          text: displayName,
          disabled: used
        }))

      return options
    }
  ),

  i2cScanning = state => state.wprsnpr.i2cScanning,
  i2cScanError = state => {
    const { type, message } = state.wprsnpr.i2cBusError

    return (type === "scan") && message
  },
  foundI2CAddresses = state => state.wprsnpr.foundI2CAddresses,

  i2cAddressStatus = createSelector([foundI2CAddresses], foundAddresses => {
    const addresses = {}
    for(let i = 7; i <= parseInt('77', 16); i++) {
      const
        hexidecimal = i.toString(16),
        address = i < 16 ?
          `0${hexidecimal}` :
          hexidecimal

      addresses[address] = !!includes(foundAddresses, i)
    }

    return addresses
  }),

  i2cDeviceInitError = state => {
    const { type } = state.wprsnpr.i2cBusError

    return (type === "deviceInit") && state.wprsnpr.i2cBusError
  },

  currentI2CSettings = createSelector([ currentBoard ], board => {
    return board.components?.i2cPorts?.[0]
  }),

  currentBoardSupportsI2C = createSelector([ currentI2CSettings ], i2cSettings => {
    return !isEmpty(i2cSettings)
  }),

  currentWipperI2CReady = createSelector(
    [ currentWipperConnected, currentBoardSupportsI2C, currentWipperSemver ],
    (connected, supported, semver) => {
      return connected && supported && semverValid(semver) && semverGte(semver, I2C_SCAN_VERSION)
    })
